/**
 * 曲名の頭文字インデックス（あかさたな + A-Z + #）のユーティリティ
 *
 * DB に読み仮名を持たないため、漢字・記号・数字で始まる曲名はすべて '#' に分類する。
 */

const KANA_ROW_HEADS = ['あ', 'か', 'さ', 'た', 'な', 'は', 'ま', 'や', 'ら', 'わ'] as const

/** 各行に属するひらがな（濁点・半濁点・小書き文字を含む） */
const KANA_ROW_MEMBERS: [string, string][] = [
  ['あ', 'あいうえおぁぃぅぇぉゔ'],
  ['か', 'かきくけこがぎぐげご'],
  ['さ', 'さしすせそざじずぜぞ'],
  ['た', 'たちつてとだぢづでどっ'],
  ['な', 'なにぬねの'],
  ['は', 'はひふへほばびぶべぼぱぴぷぺぽ'],
  ['ま', 'まみむめも'],
  ['や', 'やゆよゃゅょ'],
  ['ら', 'らりるれろ'],
  ['わ', 'わをんゎ'],
]

const KANA_TO_ROW = new Map<string, string>(
  KANA_ROW_MEMBERS.flatMap(([head, members]) => [...members].map((char): [string, string] => [char, head])),
)

const ALPHABET = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ']

/** インデックスの表示順（実際に存在するキーだけを抽出して使う） */
export const INDEX_ORDER: string[] = [...KANA_ROW_HEADS, ...ALPHABET, '#']

const INDEX_RANK = new Map(INDEX_ORDER.map((key, i) => [key, i]))

/** カタカナ1文字をひらがなに変換する（それ以外はそのまま返す） */
const toHiragana = (char: string): string => {
  const code = char.codePointAt(0) ?? 0
  if (code >= 0x30a1 && code <= 0x30f6) return String.fromCodePoint(code - 0x60)
  return char
}

/** 曲名からインデックスキー（あ / A / # など）を求める */
export const getIndexKey = (title: string): string => {
  const first = title.trim().normalize('NFKC').charAt(0)
  if (!first) return '#'

  const kanaRow = KANA_TO_ROW.get(toHiragana(first))
  if (kanaRow) return kanaRow

  const upper = first.toUpperCase()
  if (ALPHABET.includes(upper)) return upper

  return '#'
}

/** インデックスキーの並び順を比較する */
export const compareIndexKey = (a: string, b: string): number =>
  (INDEX_RANK.get(a) ?? INDEX_ORDER.length) - (INDEX_RANK.get(b) ?? INDEX_ORDER.length)
